import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';

import './FeedbackWidget.scss';

const CATEGORIES = [
  { value: 'bug', label: 'Bug', description: 'Something broken' },
  { value: 'feature', label: 'Feature request', description: 'New capability' },
  { value: 'enhancement', label: 'Enhancement', description: 'Polish / improvement' },
  { value: 'question', label: 'Question', description: 'How does this work?' }
];

const SEVERITIES = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' }
];

const STORAGE_KEY = 'identifi.preview.feedback.profile';

// Friendly labels so reviewers see "Creator Dashboard" rather than "/creator/dashboard".
const EXACT_PAGE_LABELS = {
  '/': 'Home',
  '/login': 'Login',
  '/signup': 'Sign up',
  '/verify': 'Verify phone',
  '/become-creator': 'Become a Creator',
  '/go-live': 'Go Live',
  '/go-live-now': 'Start Live Event',
  '/plans': 'Subscription Plans',
  '/mock/creator-dashboard': 'Creator Dashboard (mock)',
  '/mock/organization-dashboard': 'Organization Dashboard (mock)',
  '/creator/dashboard': 'Creator Dashboard',
  '/creator/finance': 'Creator — Revenue & Payouts',
  '/creator/content': 'Creator — Content',
  '/creator/analytics': 'Creator — Fans & CRM',
  '/creator/events': 'Creator — Live Events',
  '/creator/ads': 'Creator — Ads',
  '/creator/settings': 'Creator — Settings',
  '/org/dashboard': 'Organization Dashboard',
  '/org/team': 'Organization — Team',
  '/org/events': 'Organization — Events',
  '/org/finance': 'Organization — Finance',
  '/org/settings': 'Organization — Settings',
  '/admin/login': 'Admin Login',
  '/admin/dashboard': 'Admin Dashboard',
  '/admin/users': 'Admin — Users',
  '/admin/content': 'Admin — Content Moderation',
  '/admin/finance': 'Admin — Platform Finance',
  '/admin/settings': 'Admin — Platform Settings',
  '/admin/feedback': 'Admin — Feedback Triage',
  '/add-narratives': 'Create Narrative',
  '/add-originals': 'Create Originals',
  '/add-event': 'Create Event',
  '/add-podcast': 'Create Podcast',
  '/add-banner-ad': 'Create Banner Ad',
  '/add-widget-ad': 'Create Widget Ad',
  '/add-video-Ads': 'Create Video Ad',
  '/content-marketplace-policy': 'Content Marketplace Policy'
};

const PREFIX_PAGE_LABELS = [
  ['/id/', slug => `Creator Profile (${slug})`],
  ['/series/', () => 'Series'],
  ['/narratives/', () => 'Narrative'],
  ['/category/', slug => `Category: ${slug}`],
  ['/event/', () => 'Event'],
  ['/podcast/', () => 'Podcast'],
  ['/live/', () => 'Live Player'],
  ['/identifi-live/', () => 'Scheduled Live'],
  ['/legal/', slug => `Legal: ${slug}`],
  ['/account', () => 'Account'],
  ['/ad-manager', () => 'Ad Manager'],
  ['/search/', slug => `Search${slug ? `: ${slug}` : ''}`],
  ['/edit-', slug => `Edit: ${slug.split('/')[0]}`],
  ['/creator/', slug => `Creator: ${slug}`],
  ['/org/', slug => `Organization: ${slug}`],
  ['/admin/', slug => `Admin: ${slug}`]
];

function getPageLabel(pathname) {
  if (!pathname) return 'Unknown';
  if (EXACT_PAGE_LABELS[pathname]) return EXACT_PAGE_LABELS[pathname];
  for (const [prefix, build] of PREFIX_PAGE_LABELS) {
    if (pathname.startsWith(prefix)) {
      const slug = pathname.slice(prefix.length).replace(/\/$/, '');
      return build(slug);
    }
  }
  return pathname;
}

function loadProfile() {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return { authorName: '', authorEmail: '' };
    const parsed = JSON.parse(raw);
    return {
      authorName: parsed.authorName || '',
      authorEmail: parsed.authorEmail || ''
    };
  } catch (_err) {
    return { authorName: '', authorEmail: '' };
  }
}

function saveProfile(profile) {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    }
  } catch (_err) {
    /* ignore quota / private mode errors */
  }
}

function isWidgetElement(el) {
  if (!el || !el.closest) return false;
  return !!el.closest('.feedback-widget');
}

// Tags that carry real semantic meaning for a reviewer. When the raw element
// under the cursor is a generic wrapper (div/section/span), we'd rather tag
// the nested image, heading, button, etc. that the reviewer visually pointed
// at. elementFromPoint returns the topmost element, which is often a wrapper
// when the nested media/text is pointer-events inert or the click lands in a
// gap. elementsFromPoint gives us the full z-stack so we can pick smarter.
const MEANINGFUL_TAGS = new Set([
  'img',
  'svg',
  'picture',
  'video',
  'canvas',
  'audio',
  'button',
  'a',
  'input',
  'textarea',
  'select',
  'label',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'code',
  'pre',
  'strong',
  'em'
]);

function refineTargetAt(clientX, clientY) {
  if (typeof document === 'undefined') return null;
  const stack =
    typeof document.elementsFromPoint === 'function'
      ? document.elementsFromPoint(clientX, clientY)
      : [document.elementFromPoint(clientX, clientY)];
  const filtered = (stack || []).filter(el => el && !isWidgetElement(el));
  if (!filtered.length) return null;

  // elementsFromPoint returns the z-stack top-down (topmost first). Prefer the
  // first element in the stack whose tag is semantically meaningful (an image,
  // heading, button, etc.) so pointing at an image no longer returns the div
  // that happens to wrap it.
  for (const el of filtered) {
    const tag = (el.tagName || '').toLowerCase();
    if (MEANINGFUL_TAGS.has(tag)) return el;
  }
  // Fallback: topmost non-widget element.
  return filtered[0];
}

function buildSelector(el) {
  if (!el || el.nodeType !== 1) return '';
  if (el === document.body) return 'body';
  if (el.id) return `#${el.id}`;

  const testId = el.getAttribute && el.getAttribute('data-testid');
  if (testId) return `[data-testid="${testId}"]`;

  const parts = [];
  let current = el;
  let depth = 0;
  while (current && current !== document.body && depth < 6) {
    let part = current.tagName ? current.tagName.toLowerCase() : '';
    if (!part) break;

    if (current.id) {
      parts.unshift(`#${current.id}`);
      break;
    }

    if (current.className && typeof current.className === 'string') {
      const classes = current.className
        .split(/\s+/)
        .filter(c => c && !c.startsWith('feedback-widget'))
        .slice(0, 2);
      if (classes.length) part += `.${classes.join('.')}`;
    }

    parts.unshift(part);
    current = current.parentElement;
    depth += 1;
  }
  return parts.join(' > ');
}

function describeElement(el) {
  if (!el) return null;
  const tag = el.tagName ? el.tagName.toLowerCase() : 'element';
  const role = el.getAttribute && el.getAttribute('role');
  const ariaLabel = el.getAttribute && el.getAttribute('aria-label');
  const title = el.getAttribute && el.getAttribute('title');

  // Media elements have no textContent — fall back to alt/src so the reviewer
  // gets a useful description instead of just "<img>".
  let mediaDesc = '';
  if (tag === 'img') {
    const alt = (el.getAttribute && el.getAttribute('alt')) || '';
    const src = (el.getAttribute && el.getAttribute('src')) || '';
    const filename = src
      ? src
          .split('?')[0]
          .split('/')
          .pop()
      : '';
    mediaDesc = alt || filename || '';
  } else if (tag === 'svg') {
    const ariaTitle = el.querySelector && el.querySelector('title');
    mediaDesc = (ariaTitle && ariaTitle.textContent) || '';
  } else if (tag === 'video' || tag === 'audio' || tag === 'source') {
    mediaDesc = (el.getAttribute && el.getAttribute('src')) || '';
    if (mediaDesc)
      mediaDesc = mediaDesc
        .split('?')[0]
        .split('/')
        .pop();
  }

  const textContent = (el.textContent || '')
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, 240);
  const text = ariaLabel || title || mediaDesc || textContent;

  // Also surface image-specific metadata so the admin view can show a preview.
  const imgSrc = tag === 'img' ? (el.getAttribute && el.getAttribute('src')) || null : null;
  const imgAlt = tag === 'img' ? (el.getAttribute && el.getAttribute('alt')) || null : null;

  const rect = el.getBoundingClientRect();
  return {
    tag: role ? `${tag}[role=${role}]` : tag,
    text: text || null,
    selector: buildSelector(el),
    imgSrc,
    imgAlt,
    rect: {
      x: Math.round(rect.left),
      y: Math.round(rect.top),
      w: Math.round(rect.width),
      h: Math.round(rect.height)
    }
  };
}

const HIGHLIGHT_HASH_PREFIX = '#identifi-feedback=';
const HIGHLIGHT_CLASS = 'feedback-widget__flash-target';
const HIGHLIGHT_DURATION_MS = 3200;

function decodeHashSelector(hash) {
  if (!hash || !hash.startsWith(HIGHLIGHT_HASH_PREFIX)) return null;
  const raw = hash.slice(HIGHLIGHT_HASH_PREFIX.length);
  try {
    return decodeURIComponent(raw);
  } catch (_e) {
    return null;
  }
}

function flashElement(selector) {
  if (!selector || typeof document === 'undefined') return;
  let target = null;
  try {
    target = document.querySelector(selector);
  } catch (_e) {
    return;
  }
  if (!target) return;

  target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  target.classList.add(HIGHLIGHT_CLASS);
  window.setTimeout(() => {
    target.classList.remove(HIGHLIGHT_CLASS);
  }, HIGHLIGHT_DURATION_MS);
}

const FeedbackWidget = ({ location }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const initialProfile = useMemo(() => loadProfile(), []);
  const [category, setCategory] = useState('bug');
  const [severity, setSeverity] = useState('normal');
  const [message, setMessage] = useState('');
  const [authorName, setAuthorName] = useState(initialProfile.authorName);
  const [authorEmail, setAuthorEmail] = useState(initialProfile.authorEmail);
  const honeypotRef = useRef(null);

  const [pickerMode, setPickerMode] = useState(false);
  const [hoverRect, setHoverRect] = useState(null);
  const [hoverLabel, setHoverLabel] = useState('');
  const [selectedElement, setSelectedElement] = useState(null);

  const pathname =
    location?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const search = location?.search || (typeof window !== 'undefined' ? window.location.search : '');
  const fullPath = `${pathname}${search}`;
  const pageUrl = typeof window !== 'undefined' ? window.location.href : fullPath;
  const pageLabel = useMemo(() => getPageLabel(pathname), [pathname]);
  // Hide only on the triage page itself so admins can still file feedback
  // from the admin dashboard, users page, finance page, etc.
  const isFeedbackTriageRoute = pathname.startsWith('/admin/feedback');

  const reset = useCallback(() => {
    setCategory('bug');
    setSeverity('normal');
    setMessage('');
    setSelectedElement(null);
    setError(null);
    setSubmitted(false);
  }, []);

  const closeModal = useCallback(() => {
    setOpen(false);
    setPickerMode(false);
    setTimeout(reset, 250);
  }, [reset]);

  useEffect(() => {
    if (!open || pickerMode) return undefined;
    const onKey = event => {
      if (event.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, pickerMode, closeModal]);

  // When the admin view deep-links to an element (#identifi-feedback=<selector>),
  // scroll it into view and flash an outline so the reviewer can see it instantly.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const run = () => {
      const selector = decodeHashSelector(window.location.hash);
      if (!selector) return;
      // Delay so lazy-loaded routes / images settle before we scroll.
      window.setTimeout(() => flashElement(selector), 450);
    };

    run();
    window.addEventListener('hashchange', run);
    return () => window.removeEventListener('hashchange', run);
  }, [pathname]);

  useEffect(() => {
    if (!pickerMode) return undefined;

    const onMove = e => {
      const el = refineTargetAt(e.clientX, e.clientY);
      if (!el || isWidgetElement(el)) {
        setHoverRect(null);
        setHoverLabel('');
        return;
      }
      const rect = el.getBoundingClientRect();
      setHoverRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
      const tag = el.tagName.toLowerCase();
      // For images, showing src/alt is more informative than empty textContent.
      let preview = '';
      if (tag === 'img') {
        const alt = el.getAttribute('alt');
        const src = el.getAttribute('src') || '';
        preview = alt || (src ? src.split('/').pop() : '');
      } else {
        preview = (el.textContent || '')
          .trim()
          .replace(/\s+/g, ' ')
          .slice(0, 60);
      }
      setHoverLabel(preview ? `${tag} — ${preview}` : tag);
    };

    const onClick = e => {
      const el = refineTargetAt(e.clientX, e.clientY);
      if (!el || isWidgetElement(el)) return;
      e.preventDefault();
      e.stopPropagation();
      const described = describeElement(el);
      setSelectedElement(described);
      setPickerMode(false);
      setHoverRect(null);
      setHoverLabel('');
    };

    const onKey = e => {
      if (e.key === 'Escape') {
        setPickerMode(false);
        setHoverRect(null);
        setHoverLabel('');
      }
    };

    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [pickerMode]);

  const startPicker = () => {
    setPickerMode(true);
  };

  const clearSelectedElement = () => setSelectedElement(null);

  const handleSubmit = async event => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    const trimmedName = authorName.trim();
    const trimmedEmail = authorEmail.trim();
    saveProfile({ authorName: trimmedName, authorEmail: trimmedEmail });

    const payload = {
      category,
      severity,
      message: message.trim(),
      pageUrl,
      pagePath: fullPath,
      pageLabel,
      authorName: trimmedName || null,
      authorEmail: trimmedEmail || null,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : null,
      viewportHeight: typeof window !== 'undefined' ? window.innerHeight : null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      elementTag: selectedElement ? selectedElement.tag : null,
      elementText: selectedElement ? selectedElement.text : null,
      elementSelector: selectedElement ? selectedElement.selector : null,
      elementRect: selectedElement ? selectedElement.rect : null,
      honeypot: honeypotRef.current ? honeypotRef.current.value : ''
    };

    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        let msg = 'Something went wrong. Please try again.';
        try {
          const body = await res.json();
          if (body && body.error) msg = body.error;
          if (body && body.details && Array.isArray(body.details)) {
            msg = `${body.error || msg} ${body.details.join(' ')}`;
          }
        } catch (_e) {
          /* ignore parse errors */
        }
        if (res.status === 429) msg = 'You are submitting too quickly — please wait a moment.';
        throw new Error(msg);
      }

      setSubmitted(true);
      setMessage('');
    } catch (err) {
      setError(err.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (isFeedbackTriageRoute) return null;

  return (
    <div className="feedback-widget" data-testid="feedback-widget">
      {!open && !pickerMode && (
        <button
          type="button"
          className="feedback-widget__trigger"
          onClick={() => setOpen(true)}
          aria-label="Send feedback"
        >
          <span className="feedback-widget__trigger-icon" aria-hidden="true">
            ?
          </span>
          <span className="feedback-widget__trigger-label">Feedback</span>
        </button>
      )}

      {pickerMode && (
        <div className="feedback-widget__picker" role="dialog" aria-label="Element picker">
          <div className="feedback-widget__picker-banner">
            <strong>Click an element to tag it</strong>
            <span>Press Esc to cancel</span>
          </div>
          {hoverRect && (
            <div
              className="feedback-widget__picker-highlight"
              style={{
                top: hoverRect.top,
                left: hoverRect.left,
                width: hoverRect.width,
                height: hoverRect.height
              }}
            />
          )}
          {hoverRect && hoverLabel && (
            <div
              className="feedback-widget__picker-tooltip"
              style={{
                top: Math.max(8, hoverRect.top - 28),
                left: hoverRect.left
              }}
            >
              {hoverLabel}
            </div>
          )}
        </div>
      )}

      {open && !pickerMode && (
        <div
          className="feedback-widget__overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="feedback-widget-title"
        >
          <div className="feedback-widget__backdrop" onClick={closeModal} />
          <div className="feedback-widget__panel">
            <div className="feedback-widget__header">
              <h2 id="feedback-widget-title" className="feedback-widget__title">
                Send preview feedback
              </h2>
              <button
                type="button"
                className="feedback-widget__close"
                onClick={closeModal}
                aria-label="Close feedback form"
              >
                X
              </button>
            </div>

            {submitted ? (
              <div className="feedback-widget__success">
                <div className="feedback-widget__success-title">Thanks — we got it.</div>
                <p className="feedback-widget__success-body">
                  Your feedback was logged for <code>{pageLabel}</code>. You can submit more if
                  you&apos;d like.
                </p>
                <div className="feedback-widget__actions">
                  <button type="button" className="feedback-widget__btn" onClick={reset}>
                    Submit another
                  </button>
                  <button
                    type="button"
                    className="feedback-widget__btn feedback-widget__btn--primary"
                    onClick={closeModal}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form className="feedback-widget__form" onSubmit={handleSubmit} noValidate>
                <div className="feedback-widget__context">
                  <div className="feedback-widget__context-row">
                    <span className="feedback-widget__context-label">Page</span>
                    <strong className="feedback-widget__context-name">{pageLabel}</strong>
                  </div>
                  <div className="feedback-widget__context-row feedback-widget__context-row--muted">
                    <code title={pageUrl}>{fullPath}</code>
                  </div>
                </div>

                <div className="feedback-widget__element">
                  {selectedElement ? (
                    <>
                      <div className="feedback-widget__element-head">
                        <span className="feedback-widget__element-label">Tagged element</span>
                        <div className="feedback-widget__element-actions">
                          <button
                            type="button"
                            className="feedback-widget__link-btn"
                            onClick={startPicker}
                          >
                            Re-pick
                          </button>
                          <button
                            type="button"
                            className="feedback-widget__link-btn feedback-widget__link-btn--muted"
                            onClick={clearSelectedElement}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                      <div className="feedback-widget__element-body">
                        <code>&lt;{selectedElement.tag}&gt;</code>
                        {selectedElement.text && (
                          <span className="feedback-widget__element-text">
                            &ldquo;{selectedElement.text}&rdquo;
                          </span>
                        )}
                      </div>
                      {selectedElement.selector && (
                        <div
                          className="feedback-widget__element-selector"
                          title={selectedElement.selector}
                        >
                          {selectedElement.selector}
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      type="button"
                      className="feedback-widget__pick-btn"
                      onClick={startPicker}
                    >
                      <span className="feedback-widget__pick-icon" aria-hidden="true">
                        +
                      </span>
                      <span>
                        <strong>Point to an element</strong>
                        <small>Tell us exactly what this feedback is about</small>
                      </span>
                    </button>
                  )}
                </div>

                <div className="feedback-widget__field">
                  <span className="feedback-widget__label">Type</span>
                  <div
                    className="feedback-widget__chips"
                    role="radiogroup"
                    aria-label="Feedback type"
                  >
                    {CATEGORIES.map(c => (
                      <button
                        type="button"
                        key={c.value}
                        role="radio"
                        aria-checked={category === c.value}
                        className={`feedback-widget__chip${
                          category === c.value ? ' feedback-widget__chip--active' : ''
                        }`}
                        onClick={() => setCategory(c.value)}
                      >
                        <span className="feedback-widget__chip-label">{c.label}</span>
                        <span className="feedback-widget__chip-desc">{c.description}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="feedback-widget__field">
                  <span className="feedback-widget__label">Severity</span>
                  <div className="feedback-widget__severity-row">
                    {SEVERITIES.map(s => (
                      <button
                        type="button"
                        key={s.value}
                        className={`feedback-widget__severity${
                          severity === s.value ? ' feedback-widget__severity--active' : ''
                        } feedback-widget__severity--${s.value}`}
                        onClick={() => setSeverity(s.value)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <label className="feedback-widget__field">
                  <span className="feedback-widget__label">What&apos;s going on?</span>
                  <textarea
                    className="feedback-widget__textarea"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    rows={5}
                    placeholder="Describe the bug, idea, or question. Add steps to reproduce if it's a bug."
                    maxLength={4000}
                    required
                  />
                  <span className="feedback-widget__hint">{message.length} / 4000</span>
                </label>

                <div className="feedback-widget__field-row">
                  <label className="feedback-widget__field">
                    <span className="feedback-widget__label">Your name (optional)</span>
                    <input
                      className="feedback-widget__input"
                      type="text"
                      value={authorName}
                      onChange={e => setAuthorName(e.target.value)}
                      maxLength={120}
                      autoComplete="name"
                    />
                  </label>
                  <label className="feedback-widget__field">
                    <span className="feedback-widget__label">Email (optional)</span>
                    <input
                      className="feedback-widget__input"
                      type="email"
                      value={authorEmail}
                      onChange={e => setAuthorEmail(e.target.value)}
                      maxLength={254}
                      autoComplete="email"
                    />
                  </label>
                </div>

                <input
                  ref={honeypotRef}
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  className="feedback-widget__honeypot"
                  aria-hidden="true"
                />

                {error && <div className="feedback-widget__error">{error}</div>}

                <div className="feedback-widget__actions">
                  <button type="button" className="feedback-widget__btn" onClick={closeModal}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="feedback-widget__btn feedback-widget__btn--primary"
                    disabled={submitting || message.trim().length < 3}
                  >
                    {submitting ? 'Sending...' : 'Send feedback'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default withRouter(FeedbackWidget);
